import React, { useState, useCallback, useEffect, useContext } from "react";
import { gql, useQuery } from "urql";
import { useLocation, useParams, useSearchParams } from "wouter";
import * as Tabs from "@radix-ui/react-tabs";
import { keyBy } from "lodash";
import { Limits } from "../LimitReportPage";
import { DurationRiskTab } from "./tabs/DurationRisk";
import { ScenarioRiskTab } from "./tabs/ScenarioRisk";
import { PositionsGroupedPage } from "../PositionsGroupedPage";
import { PartyType } from "../../types.generated";
import { ClientContextSelector } from "../../contexts/ClientContextSelector";
import { ClientContext } from "../../../../components/src/contexts/ClientContext";
import { emptyObjectId } from "../../../../common/src";
import { ResponsiveTabsList } from "../../../../components/src/ResponsiveTabsList";

const getClient = gql`
    query {
        clients: parties(filter: { typeIn: [Client] }) {
            _id
            name
            accountingCurrency
            fundInfo {
                classes {
                    startDate
                }
            }
        }
    }
`;

const tabs = {
    risk: "risk",
    positions: "positions",
    limits: "limits",
    scenario: "scenario"
};

export function PortfolioRiskAndLimits(): React.ReactElement {
    const { tabId } = useParams<{ tabId: string }>();
    const [, navigate] = useLocation();
    const [activeTabId, setActiveTabId] = useState(tabId);
    const { client: clientContext } = useContext(ClientContext);
    const [queryArgs] = useSearchParams();

    const [{ fetching, error, data }] = useQuery({ query: getClient });

    useEffect(() => {
        if (clientContext && clientContext.dashName) {
            if (!(tabId in tabs)) {
                setActiveTabId("risk");
                navigate("/portfoliorisk/" + clientContext.dashName + "/risk");
            } else {
                if (tabId !== activeTabId) {
                    setActiveTabId(tabId);
                }
            }
        } /*else if (clientContext._id === emptyObjectId && (tabId || Object.keys(queryArgs).length)) {
            navigate("/portfoliorisk/" + clientContext.dashName, { replace: true });
        }*/
    }, [tabId, data, setActiveTabId, clientContext, navigate, queryArgs, activeTabId]);

    const onTabChange = useCallback(
        (tabId: string) => {
            //const types = data && data.party ? cloneDeep(data.party.types) : null;
            //const tabs = availableTabs(types);
            if (!(tabId in tabs)) {
                navigate("/portfoliorisk/" + clientContext.dashName + "/risk", { replace: true });
                setActiveTabId("risk");
            } else {
                if (tabId !== activeTabId) {
                    const url =
                        "/portfoliorisk/" + clientContext.dashName + "/" + tabId + (queryArgs.toString() ? `?${queryArgs.toString()}` : "");
                    navigate(url, { replace: true });
                    setActiveTabId(tabId);
                }
            }
        },
        [navigate, clientContext.dashName, activeTabId, queryArgs]
    );

    if (error)
        return (
            <div>
                <p>error:</p>
                <pre> {JSON.stringify(error, null, 2)}</pre>
            </div>
        );

    if (fetching)
        return (
            <div>
                <h3>Loading</h3>
            </div>
        );

    const clientsByName = keyBy(data.clients, "name");

    if (
        clientContext &&
        clientContext._id !== emptyObjectId &&
        !Object.keys(clientsByName).includes(clientContext.dashName.replace(/-/g, " "))
    ) {
        return (
            <div>
                <h3>No client with that name</h3>
            </div>
        );
    }

    return (
        <div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full sm:w-1/3 px-2 mb-4">
                    <ClientContextSelector typeIn={[PartyType.Client]} />
                </div>
            </div>
            {clientContext && clientContext._id !== emptyObjectId ? (
                <div className="flex flex-wrap -mx-2">
                    <div className="w-full px-2 bg-slate-100 rounded-lg p-4 shadow-sm">
                        <Tabs.Root value={activeTabId || "risk"} onValueChange={onTabChange} className="w-full">
                            <ResponsiveTabsList value={activeTabId || "risk"} onValueChange={onTabChange}>
                                <Tabs.Trigger
                                    value="risk"
                                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                >
                                    Risk
                                </Tabs.Trigger>
                                <Tabs.Trigger
                                    value="positions"
                                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                >
                                    Positions
                                </Tabs.Trigger>
                                <Tabs.Trigger
                                    value="limits"
                                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                >
                                    Limits
                                </Tabs.Trigger>
                                <Tabs.Trigger
                                    value="scenario"
                                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                >
                                    Scenario
                                </Tabs.Trigger>
                            </ResponsiveTabsList>
                            <Tabs.Content value="risk" className="mt-4">
                                <DurationRiskTab
                                    client={{
                                        _id: clientsByName[clientContext.dashName.replace(/-/g, " ")]._id,
                                        name: clientsByName[clientContext.dashName.replace(/-/g, " ")].name
                                    }}
                                />
                            </Tabs.Content>
                            <Tabs.Content value="positions" className="mt-4">
                                <PositionsGroupedPage />
                            </Tabs.Content>
                            <Tabs.Content value="limits" className="mt-4">
                                <Limits clientId={clientsByName[clientContext.dashName.replace(/-/g, " ")]._id} />
                            </Tabs.Content>
                            <Tabs.Content value="scenario" className="mt-4">
                                <ScenarioRiskTab
                                    client={{
                                        _id: clientsByName[clientContext.dashName.replace(/-/g, " ")]._id,
                                        name: clientsByName[clientContext.dashName.replace(/-/g, " ")].name
                                    }}
                                />
                            </Tabs.Content>
                        </Tabs.Root>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
